import { useMemo } from 'react';
import {
    useGetPCDBCatalog,
    type CatalogCategory,
    type CatalogPart,
    type CatalogPositionGroup,
    type CatalogSubcategory,
} from '../../../../hooks/useGetPCDBCatalog';
import type { PCDBSearchResult } from '../../../../hooks/useSearchPCDB';
import type { AllowablePartType, PartRule } from '../../../../hooks/useGetSettings';
import {
    normalizeProhibitedInsurance,
    normalizeRuleMakes,
    type RuleInsurance,
    type RuleMakes,
} from '../../../../components/settings/partRuleValues';
import { ALL_POSITIONS, type PositionOptions } from './PartAllowanceCells';

/**
 * One part on its way into an allowance list. The stored rule is the same four
 * fields the table edits inline, plus what the part itself offers — its
 * position groups — which the rule does not carry but the pickers need.
 */
export interface PartAllowanceDraft {
    partTerminologyId: number;
    partTerminologyName: string;
    categoryId: number;
    categoryName: string;
    subcategoryId: number;
    subcategoryName: string;
    positionIds: number[];
    positionGroupName: string;
    allPositionIds: number[];
    positionGroups: CatalogPositionGroup[];
    allowablePartTypes: AllowablePartType[];
    makes: RuleMakes;
    prohibitedInsurance: RuleInsurance;
    /** Added by "suggest similar parts" rather than picked by hand. */
    isSuggested?: boolean;
}

/**
 * Position groups per part, from the catalog. Rules carry the group they were
 * saved with but not the groups their part offers, so both the table's position
 * dropdowns and the flow's drafts read them from here — the same cached query.
 */
export const usePositionOptionsByPart = (): Map<number, PositionOptions> => {
    const { data } = useGetPCDBCatalog();
    return useMemo(() => {
        const map = new Map<number, PositionOptions>();
        for (const category of data?.categories ?? []) {
            for (const subcategory of category.subcategories) {
                for (const part of subcategory.parts) {
                    map.set(part.partTerminologyId, {
                        groups: part.positionGroups ?? [],
                        allPositionIds: part.allPositionIds ?? [],
                    });
                }
            }
        }
        return map;
    }, [data]);
};

/** A part picked from the catalog: every position, every type, every OEM. */
export const draftFromCatalogPart = (
    part: CatalogPart,
    category: CatalogCategory,
    subcategory: CatalogSubcategory,
): PartAllowanceDraft => ({
    partTerminologyId: part.partTerminologyId,
    partTerminologyName: part.partTerminologyName,
    categoryId: category.categoryId,
    categoryName: category.categoryName,
    subcategoryId: subcategory.subcategoryId,
    subcategoryName: subcategory.subcategoryName,
    positionIds: part.allPositionIds,
    positionGroupName: ALL_POSITIONS,
    allPositionIds: part.allPositionIds,
    positionGroups: part.positionGroups ?? [],
    allowablePartTypes: [],
    makes: 'all',
    prohibitedInsurance: undefined,
});

/** The same, for a part found through the global search rather than browsing. */
export const draftFromSearchResult = (
    result: PCDBSearchResult,
    isSuggested = false,
): PartAllowanceDraft => ({
    partTerminologyId: result.partTerminologyId,
    partTerminologyName: result.partTerminologyName,
    categoryId: result.categoryId,
    categoryName: result.categoryName,
    subcategoryId: result.subcategoryId,
    subcategoryName: result.subcategoryName,
    positionIds: result.allPositionIds,
    positionGroupName: ALL_POSITIONS,
    allPositionIds: result.allPositionIds,
    positionGroups: result.positionGroups ?? [],
    allowablePartTypes: [],
    makes: 'all',
    ...(isSuggested ? { isSuggested: true } : {}),
    prohibitedInsurance: undefined,
});

/**
 * A saved rule, reopened for editing. The position groups come from the
 * catalog, which is a separate query, so a draft built before it lands gets
 * them filled in afterwards by {@link withPositionOptions}.
 */
export const draftFromRule = (
    rule: PartRule,
    oemOptions: Array<{ _id: string; name: string }>,
    options?: PositionOptions,
): PartAllowanceDraft => ({
    partTerminologyId: rule.partTerminologyId,
    partTerminologyName: rule.partTerminologyName,
    categoryId: rule.categoryId,
    categoryName: rule.categoryName,
    subcategoryId: rule.subcategoryId,
    subcategoryName: rule.subcategoryName,
    positionIds: rule.positionIds,
    positionGroupName: rule.positionGroupName || ALL_POSITIONS,
    allPositionIds: options?.allPositionIds ?? rule.positionIds,
    positionGroups: options?.groups ?? [],
    allowablePartTypes: rule.allowablePartTypes ?? [],
    makes: normalizeRuleMakes(rule.makes, oemOptions),
    prohibitedInsurance: normalizeProhibitedInsurance(rule.prohibitedInsurance),
});

/** Fills in a draft's position groups once the catalog has arrived. */
export const withPositionOptions = (
    draft: PartAllowanceDraft,
    options: PositionOptions | undefined,
): PartAllowanceDraft => {
    if (!options || draft.positionGroups.length > 0) return draft;
    return {
        ...draft,
        positionGroups: options.groups,
        allPositionIds: options.allPositionIds.length > 0
            ? options.allPositionIds
            : draft.allPositionIds,
    };
};

/**
 * The stored rule. Prohibited insurance is only written for the list that
 * offers it, so moving a rule elsewhere never carries a field that list cannot
 * act on.
 */
export const draftToRule = (
    draft: PartAllowanceDraft,
    showInsurance: boolean,
): PartRule => ({
    categoryId: draft.categoryId,
    categoryName: draft.categoryName,
    subcategoryId: draft.subcategoryId,
    subcategoryName: draft.subcategoryName,
    partTerminologyId: draft.partTerminologyId,
    partTerminologyName: draft.partTerminologyName,
    positionIds: draft.positionIds,
    positionGroupName: draft.positionGroupName,
    allowablePartTypes: draft.allowablePartTypes,
    makes: draft.makes,
    ...(showInsurance && draft.prohibitedInsurance !== undefined
        ? { prohibitedInsurance: draft.prohibitedInsurance }
        : {}),
});
